import type { Character } from '@dcc/rules/domain/character.ts';
import type { RuntimeState } from '@dcc/rules/domain/runtime.ts';
import { inEncounter } from '@dcc/rules/domain/runtime.ts';
import type { Item, Skill } from '@dcc/rules/content/types.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { ownershipOf } from '@dcc/rules/app/hotlistService.ts';
import {
  defaultHotlistAllocation,
  hotlistAddProblem,
  hotlistAllocation,
  type HotlistEntry,
} from '@dcc/rules/rules/hotlist.ts';
import { inHandCopies, unallocatedCopies } from '@dcc/rules/rules/inventory.ts';
import {
  chooseUseRoute,
  physicalUseContext,
  planItemUse,
  type ItemUsePlan,
  type ItemUseRequest,
} from '@dcc/rules/rules/itemUse.ts';
import { isPassiveSpell, spellCastActionCost } from '@dcc/rules/rules/spellCast.ts';
import { isRoomItemId } from './roomInventory.ts';


export interface InventoryAddChoice {
  item: Item;
  ownedQuantity: number;
}

/**
 * Every live catalogue Item stays available in the Add picker, including Items
 * the crawler already owns. Inventory ownership is a quantity, not a one-time
 * unlock; addItem() merges another copy into the existing entry.
 */
export function inventoryAddChoices(character: Character): InventoryAddChoice[] {
  const owned = new Map(character.inventory.map((entry) => [entry.itemId, entry.quantity]));
  return contentRegistry.allItems()
    // Items that belong to the ROOM (loot, GM awards, the Party Inventory) are
    // acquired by being given or claimed, never by picking them from a list.
    .filter((item) => !isRoomItemId(item.id))
    .map((item) => ({ item, ownedQuantity: owned.get(item.id) ?? 0 }))
    .sort((a, b) => a.item.name.localeCompare(b.item.name));
}

/** Live catalogue adapters. The imported registry binding is replaced when custom content loads. */
export const inventoryCatalogue = {
  lookupItem: (id: string) => contentRegistry.getItem(id),
  lookupAttack: (id: string) => contentRegistry.getAttack(id),
};

export const hotlistCatalogue = {
  lookupItem: (id: string) => contentRegistry.getItem(id),
  lookupSkill: (id: string) => contentRegistry.getSkill(id),
};

export interface HotlistCandidate {
  key: string;
  name: string;
  entry: HotlistEntry;
  /** Item-only default. Skills and Spells carry null. */
  defaultQuantity: number | null;
}

/**
 * Things the crawler can legally put into this Hotlist slot right now.
 *
 * This is an interface helper, not a second rules engine: every candidate is
 * admitted by `hotlistAddProblem`, the same rule the service rechecks when the
 * player presses Add. The list may become stale between render and click; the
 * service remains authoritative.
 */
export function hotlistCandidatesFor(character: Character, slot: number): HotlistCandidate[] {
  const ownership = ownershipOf(character);
  const candidates: HotlistCandidate[] = [];

  for (const owned of character.inventory) {
    const item = contentRegistry.getItem(owned.itemId);
    if (!item) continue;
    const available = ownership.availableCopies(owned.itemId);
    const quantity = defaultHotlistAllocation(item, available);
    const entry: HotlistEntry = { kind: 'item', itemId: owned.itemId, quantity };
    if (hotlistAddProblem(character.hotlist, slot, entry, hotlistCatalogue, ownership) !== null) continue;
    candidates.push({ key: `item:${owned.itemId}`, name: item.name, entry, defaultQuantity: quantity });
  }

  for (const known of character.skills) {
    const skill = contentRegistry.getSkill(known.skillId);
    if (!skill || skill.kind !== 'spell') continue;
    const entry: HotlistEntry = skill.kind === 'spell'
      ? { kind: 'spell', skillId: known.skillId }
      : { kind: 'skill', skillId: known.skillId };
    if (hotlistAddProblem(character.hotlist, slot, entry, hotlistCatalogue, ownership) !== null) continue;
    const label = entry.kind === 'spell'
      ? 'Spell'
      : contentRegistry.getAttack(skill.id)
        ? 'Attack Skill'
        : 'Skill';
    candidates.push({
      key: `${entry.kind}:${known.skillId}`,
      name: `${skill.name} (${label})`,
      entry,
      defaultQuantity: null,
    });
  }

  return candidates.sort((a, b) => a.name.localeCompare(b.name));
}

export interface ItemUsePreview {
  request: ItemUseRequest;
  plan: ItemUsePlan;
  label: string;
}

/**
 * Build the visible source + legality preview for one consumable use.
 * `forcedRoute` is used by a Hotlist slot's explicit Use button; Inventory uses
 * the shared route chooser so an already-held copy wins, then a prepared copy,
 * then a genuinely free Inventory copy.
 */
export function previewItemUse(
  character: Character,
  runtime: RuntimeState,
  itemId: string,
  forcedRoute?: ItemUseRequest['route'],
  forcedSlot: number | null = null,
): ItemUsePreview {
  const item = contentRegistry.getItem(itemId);
  const entry = character.inventory.find((candidate) => candidate.itemId === itemId);
  const hotlistSlot = character.hotlist.findIndex(
    (candidate) => candidate !== null && candidate.kind === 'item' && candidate.itemId === itemId,
  );
  const fighting = inEncounter(runtime);
  const route = forcedRoute ?? chooseUseRoute({
    fighting,
    hasHotlistSlot: hotlistSlot >= 0,
    availableCopies: entry ? unallocatedCopies(entry) : 0,
    hotlistedCopies: hotlistAllocation(character.hotlist, itemId),
    inHandCopies: entry ? inHandCopies(entry, item) : 0,
  });
  const slot = route === 'hotlist'
    ? (forcedSlot ?? (hotlistSlot >= 0 ? hotlistSlot : null))
    : null;

  const request: ItemUseRequest = route === 'hotlist' && slot !== null
    ? { itemId, route: 'hotlist', slot }
    : route === 'hand'
      ? { itemId, route: 'hand', slot: null }
      : { itemId, route: 'inventory', slot: null };

  const context = physicalUseContext(character, itemId, runtime, fighting, request.route, request.slot, item);
  const plan = planItemUse(item, runtime, context);
  const label = request.route === 'hand'
    ? 'Use from hand'
    : request.route === 'hotlist'
      ? 'Use prepared'
      : 'Use from Inventory';

  return { request, plan, label };
}

/** Passive known Spells belong on the support-spell surface, not the Attack tab. */
export function supportSpellsFor(character: Character): Skill[] {
  return character.skills.flatMap((known) => {
    const skill = contentRegistry.getSkill(known.skillId);
    return skill && skill.kind === 'spell' && isPassiveSpell(skill) ? [skill] : [];
  });
}

export function supportSpellActionCost(character: Character, skillId: string): 1 | 2 {
  return spellCastActionCost(character, skillId, hotlistCatalogue);
}
